"use client"

import * as React from "react"
import { useRouter } from "next/navigation"
import { motion, type Transition } from "motion/react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cn } from "@/lib/utils"
import { T } from "gt-next"

// Hook to detect touch device - checks if primary input is touch
const useIsTouchDevice = () => {
    const [isTouch, setIsTouch] = React.useState(false)

    React.useEffect(() => {
        // Check for touch capability
        const checkTouch = () => {
            setIsTouch(window.matchMedia("(pointer: coarse)").matches)
        }
        checkTouch()

        // Listen for changes (e.g., connecting/disconnecting touch devices)
        const mediaQuery = window.matchMedia("(pointer: coarse)")
        mediaQuery.addEventListener("change", checkTouch)
        return () => mediaQuery.removeEventListener("change", checkTouch)
    }, [])

    return isTouch
}

// Context for sharing AvatarGroup state
const AvatarGroupContext = React.createContext<{
    translate: string | number
    transition: Transition
    tooltipTransition: Transition
    openDelay: number
    closeDelay: number
    side: "top" | "bottom" | "left" | "right"
    sideOffset: number
    align: "start" | "center" | "end"
    alignOffset: number
    // Mobile touch support
    isTouch: boolean
    activeItemId: string | null
    setActiveItemId: (id: string | null) => void
    // Scrub gesture support
    registerItem: (id: string, element: HTMLElement) => void
    unregisterItem: (id: string) => void
    isScrubbing: boolean
} | null>(null)

const useAvatarGroup = () => {
    const context = React.useContext(AvatarGroupContext)
    if (!context) {
        throw new Error("AvatarGroup components must be used within an AvatarGroup")
    }
    return context
}

// Types
interface AvatarGroupProps extends Omit<React.ComponentProps<"div">, "translate"> {
    invertOverlap?: boolean
    translate?: string | number
    transition?: Transition
    tooltipTransition?: Transition
    openDelay?: number
    closeDelay?: number
    side?: "top" | "bottom" | "left" | "right"
    sideOffset?: number
    align?: "start" | "center" | "end"
    alignOffset?: number
}

interface AvatarGroupItemProps extends Omit<React.ComponentProps<typeof motion.div>, "children"> {
    children?: React.ReactNode
}

interface AvatarGroupTooltipProps extends React.ComponentProps<typeof TooltipPrimitive.Content> {}

// Scrub gesture threshold (pixels) - movement beyond this is considered a scrub, not a tap
const SCRUB_THRESHOLD = 8

// AvatarGroup - Main container
const AvatarGroup = React.forwardRef<HTMLDivElement, AvatarGroupProps>(
    (
        {
            children,
            className,
            invertOverlap = true,
            translate = "-30%",
            transition = { type: "spring", stiffness: 300, damping: 17 },
            tooltipTransition = { type: "spring", stiffness: 300, damping: 35 },
            openDelay = 0,
            closeDelay = 0,
            side = "top",
            sideOffset = 10,
            align = "center",
            alignOffset = 0,
            ...props
        },
        ref
    ) => {
        const childrenArray = React.Children.toArray(children)
        const isTouch = useIsTouchDevice()
        const [activeItemId, setActiveItemId] = React.useState<string | null>(null)
        const [isScrubbing, setIsScrubbing] = React.useState(false)
        const containerRef = React.useRef<HTMLDivElement>(null)

        // Item registration for hit-testing during scrub gesture
        const itemsRef = React.useRef<Map<string, HTMLElement>>(new Map())
        const registerItem = React.useCallback((id: string, element: HTMLElement) => {
            itemsRef.current.set(id, element)
        }, [])
        const unregisterItem = React.useCallback((id: string) => {
            itemsRef.current.delete(id)
        }, [])

        // Scrub gesture state (refs for performance - no re-renders during gesture)
        const touchStartRef = React.useRef<{ x: number; y: number } | null>(null)
        const cachedRectsRef = React.useRef<Map<string, DOMRect> | null>(null)
        const isScrubbingRef = React.useRef(false)
        const lastActiveIdRef = React.useRef<string | null>(null)

        // Cache bounding rects for all registered items (called once on touchstart)
        const cacheItemRects = React.useCallback(() => {
            const rects = new Map<string, DOMRect>()
            itemsRef.current.forEach((element, id) => {
                rects.set(id, element.getBoundingClientRect())
            })
            cachedRectsRef.current = rects
        }, [])

        // Find which item is under a given touch point
        const findItemAtPoint = React.useCallback((x: number, y: number): string | null => {
            if (!cachedRectsRef.current) return null

            for (const [id, rect] of cachedRectsRef.current) {
                // Check if point is within this item's bounds
                if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
                    return id
                }
            }
            return null
        }, [])

        // Touch gesture handlers
        React.useEffect(() => {
            if (!isTouch) return

            const container = containerRef.current
            if (!container) return

            const handleTouchStart = (e: TouchEvent) => {
                const touch = e.touches[0]
                touchStartRef.current = { x: touch.clientX, y: touch.clientY }
                isScrubbingRef.current = false
                lastActiveIdRef.current = activeItemId
                // Cache all item rects at the start for fast hit-testing
                cacheItemRects()
            }

            const handleTouchMove = (e: TouchEvent) => {
                if (!touchStartRef.current) return

                const touch = e.touches[0]
                const deltaX = Math.abs(touch.clientX - touchStartRef.current.x)
                const deltaY = Math.abs(touch.clientY - touchStartRef.current.y)

                // Check if movement exceeds threshold - if so, we're scrubbing
                if (!isScrubbingRef.current && (deltaX > SCRUB_THRESHOLD || deltaY > SCRUB_THRESHOLD)) {
                    isScrubbingRef.current = true
                    setIsScrubbing(true)
                }

                if (isScrubbingRef.current) {
                    // Find which item is under the current touch point
                    const itemUnderTouch = findItemAtPoint(touch.clientX, touch.clientY)

                    // Only update if the active item changed (performance optimization)
                    if (itemUnderTouch !== lastActiveIdRef.current) {
                        lastActiveIdRef.current = itemUnderTouch
                        setActiveItemId(itemUnderTouch)
                    }
                }
            }

            const handleTouchEnd = () => {
                // Small delay before clearing scrubbing state to prevent tap from firing
                if (isScrubbingRef.current) {
                    setTimeout(() => {
                        setIsScrubbing(false)
                    }, 50)
                }
                touchStartRef.current = null
                cachedRectsRef.current = null
                isScrubbingRef.current = false
            }

            // Use passive listeners for better scroll performance
            container.addEventListener("touchstart", handleTouchStart, { passive: true })
            container.addEventListener("touchmove", handleTouchMove, { passive: true })
            container.addEventListener("touchend", handleTouchEnd, { passive: true })
            container.addEventListener("touchcancel", handleTouchEnd, { passive: true })

            return () => {
                container.removeEventListener("touchstart", handleTouchStart)
                container.removeEventListener("touchmove", handleTouchMove)
                container.removeEventListener("touchend", handleTouchEnd)
                container.removeEventListener("touchcancel", handleTouchEnd)
            }
        }, [isTouch, activeItemId, cacheItemRects, findItemAtPoint])

        // Click outside handler for touch devices - dismisses active avatar
        React.useEffect(() => {
            if (!isTouch || !activeItemId) return

            const handleClickOutside = (event: MouseEvent | TouchEvent) => {
                if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                    setActiveItemId(null)
                }
            }

            // Use touchend for better mobile experience (fires after tap completes)
            document.addEventListener("touchend", handleClickOutside)
            document.addEventListener("mousedown", handleClickOutside)

            return () => {
                document.removeEventListener("touchend", handleClickOutside)
                document.removeEventListener("mousedown", handleClickOutside)
            }
        }, [isTouch, activeItemId])

        // Combine refs
        const setRefs = React.useCallback(
            (node: HTMLDivElement | null) => {
                containerRef.current = node
                if (typeof ref === "function") {
                    ref(node)
                } else if (ref) {
                    ref.current = node
                }
            },
            [ref]
        )

        return (
            <TooltipPrimitive.Provider delayDuration={openDelay}>
                <AvatarGroupContext.Provider
                    value={{
                        translate,
                        transition,
                        tooltipTransition,
                        openDelay,
                        closeDelay,
                        side,
                        sideOffset,
                        align,
                        alignOffset,
                        isTouch,
                        activeItemId,
                        setActiveItemId,
                        registerItem,
                        unregisterItem,
                        isScrubbing
                    }}>
                    <div ref={setRefs} className={cn("flex items-center -space-x-3", className)} {...props}>
                        {childrenArray.map((child, index) => {
                            // Check if this is the AvatarGroupYou component - give it highest z-index
                            const isYouAvatar = React.isValidElement(child) && (child.type as React.ComponentType)?.displayName === "AvatarGroupYou"

                            return (
                                <div
                                    key={index}
                                    className="relative"
                                    style={{
                                        zIndex: isYouAvatar ? childrenArray.length + 1 : invertOverlap ? childrenArray.length - index : index + 1
                                    }}>
                                    {child}
                                </div>
                            )
                        })}
                    </div>
                </AvatarGroupContext.Provider>
            </TooltipPrimitive.Provider>
        )
    }
)
AvatarGroup.displayName = "AvatarGroup"

// AvatarGroupItem - Individual avatar wrapper with hover animation
const AvatarGroupItem = React.forwardRef<HTMLDivElement, AvatarGroupItemProps>(({ children, className, ...props }, ref) => {
    const { translate, transition, side, sideOffset, align, alignOffset, isTouch, activeItemId, setActiveItemId, registerItem, unregisterItem, isScrubbing } = useAvatarGroup()
    const [isHovered, setIsHovered] = React.useState(false)
    const elementRef = React.useRef<HTMLDivElement>(null)

    // Generate stable unique ID for this item
    const itemId = React.useId()

    // Register element with parent for scrub hit-testing
    React.useEffect(() => {
        const element = elementRef.current
        if (element && isTouch) {
            registerItem(itemId, element)
            return () => unregisterItem(itemId)
        }
    }, [itemId, isTouch, registerItem, unregisterItem])

    // Combine refs
    const setRefs = React.useCallback(
        (node: HTMLDivElement | null) => {
            elementRef.current = node
            if (typeof ref === "function") {
                ref(node)
            } else if (ref) {
                ref.current = node
            }
        },
        [ref]
    )

    // Determine if this item is active (for touch) or hovered (for mouse)
    const isActive = isTouch ? activeItemId === itemId : isHovered

    // Handle tap on touch devices (disabled during scrub)
    const handleTap = React.useCallback(() => {
        if (isTouch && !isScrubbing) {
            // Toggle: if already active, deactivate; otherwise activate this item
            setActiveItemId(activeItemId === itemId ? null : itemId)
        }
    }, [isTouch, isScrubbing, activeItemId, itemId, setActiveItemId])

    // Separate avatar content from tooltip content
    const childArray = React.Children.toArray(children)
    let tooltipChildren: React.ReactNode = null
    const avatarContent: React.ReactNode[] = []

    childArray.forEach((child) => {
        if (React.isValidElement(child) && child.type === AvatarGroupTooltip) {
            tooltipChildren = (child.props as { children: React.ReactNode }).children
        } else {
            avatarContent.push(child as React.ReactNode)
        }
    })

    return (
        <TooltipPrimitive.Root open={isTouch ? isActive : undefined}>
            <TooltipPrimitive.Trigger asChild>
                <motion.div
                    ref={setRefs}
                    className={cn("cursor-pointer", className)}
                    onHoverStart={() => !isTouch && setIsHovered(true)}
                    onHoverEnd={() => !isTouch && setIsHovered(false)}
                    onTap={handleTap}
                    animate={{
                        y: isActive ? translate : 0,
                        scale: isActive ? 1.05 : 1
                    }}
                    transition={transition}
                    {...props}>
                    {avatarContent}
                </motion.div>
            </TooltipPrimitive.Trigger>
            {tooltipChildren && (
                <TooltipPrimitive.Portal>
                    <TooltipPrimitive.Content side={side} sideOffset={sideOffset} align={align} alignOffset={alignOffset} className="z-50 rounded-lg bg-gray-900 px-3 py-1.5 text-sm text-white shadow-lg animate-in fade-in-0 zoom-in-95">
                        {tooltipChildren}
                    </TooltipPrimitive.Content>
                </TooltipPrimitive.Portal>
            )}
        </TooltipPrimitive.Root>
    )
})
AvatarGroupItem.displayName = "AvatarGroupItem"

// AvatarGroupTooltip - Marker component for tooltip content (rendered by AvatarGroupItem)
const AvatarGroupTooltip = ({ children }: { children: React.ReactNode }) => {
    // This component is just a marker - actual rendering is handled by AvatarGroupItem
    return <>{children}</>
}
AvatarGroupTooltip.displayName = "AvatarGroupTooltip"

// Simple Avatar component for use with AvatarGroup (without Radix dependency)
interface SimpleAvatarProps extends React.ComponentProps<"div"> {
    src?: string
    alt?: string
    fallback?: string
    size?: "sm" | "md" | "lg"
}

const SimpleAvatar = React.forwardRef<HTMLDivElement, SimpleAvatarProps>(({ src, alt, fallback, size = "md", className, children, ...props }, ref) => {
    const [imageError, setImageError] = React.useState(false)

    const sizeClasses = {
        sm: "h-9 w-9 sm:h-8 sm:w-8 text-xs",
        md: "h-11 w-11 sm:h-10 sm:w-10 text-sm",
        lg: "h-14 w-14 sm:h-12 sm:w-12 text-base"
    }

    return (
        <div ref={ref} className={cn("relative flex shrink-0 overflow-hidden rounded-full ring-2 ring-white", sizeClasses[size], className)} {...props}>
            {src && !imageError ? (
                <img src={src} alt={alt || fallback || "Avatar"} className="img-outline aspect-square h-full w-full rounded-full object-cover pointer-events-none select-none [.ring-ink>&]:outline-white/10" draggable={false} onError={() => setImageError(true)} />
            ) : (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 font-medium text-white">{fallback || alt?.charAt(0)?.toUpperCase() || "?"}</div>
            )}
            {children}
        </div>
    )
})
SimpleAvatar.displayName = "SimpleAvatar"

// AvatarGroupYou - Special "YOU?" placeholder avatar for social proof
interface AvatarGroupYouProps extends Omit<React.ComponentProps<typeof motion.div>, "children"> {
    size?: "sm" | "md" | "lg"
    tooltipText?: string
    href?: string
    /** Use "light" variant for light/pink backgrounds (white ring) */
    variant?: "default" | "light"
}

const AvatarGroupYou = React.forwardRef<HTMLDivElement, AvatarGroupYouProps>(({ size = "md", tooltipText = "You could be next!", href = "/pricing", variant = "default", className, ...props }, ref) => {
    const router = useRouter()
    const { translate, transition, side, sideOffset, align, alignOffset, isTouch, activeItemId, setActiveItemId, registerItem, unregisterItem, isScrubbing } = useAvatarGroup()
    const [isHovered, setIsHovered] = React.useState(false)
    const elementRef = React.useRef<HTMLDivElement>(null)

    // Generate stable unique ID for this item
    const itemId = React.useId()

    // Register element with parent for scrub hit-testing
    React.useEffect(() => {
        const element = elementRef.current
        if (element && isTouch) {
            registerItem(itemId, element)
            return () => unregisterItem(itemId)
        }
    }, [itemId, isTouch, registerItem, unregisterItem])

    // Combine refs
    const setRefs = React.useCallback(
        (node: HTMLDivElement | null) => {
            elementRef.current = node
            if (typeof ref === "function") {
                ref(node)
            } else if (ref) {
                ref.current = node
            }
        },
        [ref]
    )

    // Determine if this item is active (for touch) or hovered (for mouse)
    const isActive = isTouch ? activeItemId === itemId : isHovered

    // Handle tap on touch devices - first tap shows tooltip, second tap navigates (disabled during scrub)
    const handleTap = React.useCallback(() => {
        if (isTouch && !isScrubbing) {
            if (activeItemId === itemId) {
                // Already active - navigate on second tap
                if (href) router.push(href)
            } else {
                // Not active - activate to show tooltip
                setActiveItemId(itemId)
            }
        }
    }, [isTouch, isScrubbing, activeItemId, itemId, setActiveItemId, href, router])

    // Handle click for desktop (direct navigation)
    const handleClick = React.useCallback(() => {
        if (!isTouch && href) {
            router.push(href)
        }
    }, [isTouch, href, router])

    const sizeClasses = {
        sm: "h-9 w-9 sm:h-8 sm:w-8 text-[10px]",
        md: "h-11 w-11 sm:h-10 sm:w-10 text-xs",
        lg: "h-14 w-14 sm:h-12 sm:w-12 text-sm"
    }

    return (
        <TooltipPrimitive.Root open={isTouch ? isActive : undefined}>
            <TooltipPrimitive.Trigger asChild>
                <motion.div
                    ref={setRefs}
                    className={cn("cursor-pointer", className)}
                    onHoverStart={() => !isTouch && setIsHovered(true)}
                    onHoverEnd={() => !isTouch && setIsHovered(false)}
                    onTap={handleTap}
                    onClick={handleClick}
                    animate={{
                        y: isActive ? translate : 0,
                        scale: isActive ? 1.05 : 1
                    }}
                    transition={transition}
                    {...props}>
                    <div className={cn("relative flex shrink-0 items-center justify-center rounded-full", variant === "light" ? "" : "ring-2 ring-[rgb(20,20,20)]", "group", sizeClasses[size])} style={{ background: "#141414" }}>
                        {/* Rotating dashed border */}
                        <motion.div
                            className={cn(
                                "absolute inset-0 rounded-full border-2 border-dashed",
                                variant === "light" ? "border-white" : "border-[rgb(255,163,209)]/60"
                            )}
                            animate={{ rotate: 360 }}
                            transition={{
                                duration: 12,
                                repeat: Infinity,
                                ease: "linear"
                            }}
                        />
                        {/* Subtle pulse ring animation */}
                        <motion.div
                            className={cn(
                                "absolute inset-0 rounded-full border-2",
                                variant === "light" ? "border-white/60" : "border-[rgb(255,163,209)]/40"
                            )}
                            animate={{
                                scale: [1, 1.15, 1],
                                opacity: [0.6, 0, 0.6]
                            }}
                            transition={{
                                duration: 2.5,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        />
                        {/* YOU? text */}
                        <span className="font-semibold tracking-tight text-[rgb(255,163,209)] transition-transform group-hover:scale-105"><T>YOU?</T></span>
                    </div>
                </motion.div>
            </TooltipPrimitive.Trigger>
            <TooltipPrimitive.Portal>
                <TooltipPrimitive.Content 
                    side={side} 
                    sideOffset={sideOffset} 
                    align={align} 
                    alignOffset={alignOffset} 
                    className={cn(
                        "z-50 rounded-lg px-3 py-1.5 text-sm font-medium shadow-lg animate-in fade-in-0 zoom-in-95",
                        variant === "light" 
                            ? "bg-white text-ink" 
                            : "bg-gradient-to-r from-[rgb(255,163,209)] to-[#fc83bf] text-[rgb(20,20,20)]"
                    )}
                >
                    {tooltipText}
                </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
        </TooltipPrimitive.Root>
    )
})
AvatarGroupYou.displayName = "AvatarGroupYou"

export { AvatarGroup, AvatarGroupItem, AvatarGroupTooltip, AvatarGroupYou, SimpleAvatar, useAvatarGroup }
